import { useState } from "react";
import { SentimentEvent, SentimentPost } from "../../api";
import {
  SOURCE_BADGE,
  SENT_META,
  IMPACT_META,
  TREND_META,
  ISSUE_TYPE_META,
} from "./shared";

interface Props {
  events: SentimentEvent[];
  loading: boolean;
}

export default function EventTimeline({ events, loading }: Props) {
  const [expanded, setExpanded] = useState<number | null>(null);

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-xl bg-surface" />
        ))}
      </div>
    );
  }
  if (!events.length) return <div className="py-8 text-center text-[13px] text-ink-faint">暂无舆情事件</div>;

  return (
    <div className="space-y-2">
      {events.map((ev, i) => {
        const meta = IMPACT_META[ev.impact ?? "medium"] ?? IMPACT_META.medium;
        const isOpen = expanded === i;
        const dateLabel = ev.date_start === ev.date_end || !ev.date_end
          ? ev.date_start?.slice(0, 10) ?? ""
          : `${ev.date_start?.slice(0, 10)} — ${ev.date_end?.slice(0, 10)}`;
        const issueMeta = ev.issue_type ? ISSUE_TYPE_META[ev.issue_type] : null;
        const trendMeta = ev.trend ? TREND_META[ev.trend] : null;

        return (
          <div key={i} className={`rounded-xl border transition-colors ${isOpen ? "border-border-strong bg-surface" : "border-transparent hover:border-border hover:bg-surface"}`}>
            <button
              onClick={() => setExpanded(isOpen ? null : i)}
              className="flex w-full items-start gap-2.5 px-3.5 py-3 text-left"
            >
              <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${meta.dot}`} />
              <div className="min-w-0 flex-1">
                <p className="line-clamp-1 text-[13px] font-medium leading-snug text-ink">{ev.title}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-mono tabular-nums text-ink-faint">{dateLabel}</span>
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${meta.badge}`}>{meta.label}</span>
                  {issueMeta && <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${issueMeta.cls}`}>{issueMeta.label}</span>}
                  {trendMeta && <span className={`text-[10px] font-medium ${trendMeta.cls}`}>{trendMeta.label}</span>}
                  {ev.posts?.length > 0 && <span className="text-[10px] text-ink-faint">{ev.posts.length} 条帖子</span>}
                </div>
              </div>
              <svg
                className={`mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-faint transition-transform ${isOpen ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 16 16"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6l4 4 4-4" />
              </svg>
            </button>

            {isOpen && (
              <div className="space-y-2.5 px-3.5 pb-3.5">
                {ev.summary && <p className="pl-4 text-[12px] leading-relaxed text-ink-secondary">{ev.summary}</p>}

                {ev.representative_quote && (
                  <div className="ml-4 border-l-2 border-border pl-4">
                    <p className="text-[12px] italic leading-relaxed text-ink-tertiary">"{ev.representative_quote}"</p>
                  </div>
                )}

                {ev.severity_factors && ev.severity_factors.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 pl-4">
                    <span className="text-[10px] text-ink-faint">风险因素</span>
                    {ev.severity_factors.map((f, fi) => (
                      <span
                        key={fi}
                        className="rounded-full border border-rose-200/70 bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                )}

                {ev.posts?.length > 0 && (
                  <div className="space-y-1.5 pl-4">
                    {ev.posts.slice(0, 10).map((p: SentimentPost) => {
                      const src = SOURCE_BADGE[p.source_code] ?? { label: "帖", cls: "bg-ink-faint text-white" };
                      const sent = SENT_META[p.sentiment] ?? SENT_META.neutral;
                      return (
                        <a
                          key={p.id}
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group block rounded-lg px-2 py-1.5 transition-colors hover:bg-surface-raised"
                        >
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-[9px] font-bold ${src.cls}`}>
                              {src.label}
                            </span>
                            <span className="line-clamp-1 flex-1 text-[12px] text-ink-secondary transition-colors group-hover:text-primary-600 dark:group-hover:text-primary-400">
                              {p.title}
                            </span>
                            <span className="shrink-0 text-[10px] font-mono tabular-nums text-ink-faint">{p.date?.slice(0, 10)}</span>
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-ink-tertiary">
                              <span className={`h-1 w-1 rounded-full ${sent.dot}`} />
                              {sent.label}
                            </span>
                          </div>
                          {p.summary && p.summary !== p.title && (
                            <p className="mt-0.5 pl-6 line-clamp-1 text-[11px] leading-relaxed text-ink-faint">{p.summary}</p>
                          )}
                        </a>
                      );
                    })}
                    {ev.posts.length > 10 && <p className="pl-6 text-[11px] text-ink-faint">还有 {ev.posts.length - 10} 条…</p>}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
